import { useEffect, useRef, useState } from 'react';
import type { QuestionModel } from '../models/Question.js';
import { useApi } from '../services/api.js';
import { log } from '../utils/log';

export const useQuestions = (reviewId?: number, isAuthenticated = false) => {
  const api = useApi();
  const [questions, setQuestions] = useState<QuestionModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const hasFetchedReference = useRef(false);

  const fetchQuestions = async () => {
    if (!reviewId || !isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get<{ data: QuestionModel[] }>(`/question/review/${reviewId}`);

      // Handle different response structures
      if (Array.isArray(response)) {
        setQuestions(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setQuestions(response.data);
      } else {
        setQuestions([]);
      }
    } catch (error_: any) {
      log.error('Error fetching questions:', error_);
      setError('Failed to load questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (reviewId && isAuthenticated && !hasFetchedReference.current) {
      hasFetchedReference.current = true;
      fetchQuestions();
    }
  }, [reviewId, isAuthenticated]);

  return {
    error,
    loading,
    questions,
    refetch: fetchQuestions,
  };
};
